import React, { useEffect, useRef, useMemo } from "react";
import "./RecapVideos.css";
import {
  fetchRecapMaterials,
  fetchRecapMaterialByCohortSlug,
} from "../../../redux/slices/classRecapMaterialSlice";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useNavigate } from "react-router-dom";

function OrgList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const recapMaterials = useSelector(
    (state) => state.classRecapMaterials.recapMaterials,
  );
  const loading = useSelector((state) => state.classRecapMaterials.loading);
  const cohortSlug = recapMaterials.cohort?.slug;
  const lastFetchedSlug = useRef();

  useEffect(() => {
    dispatch(fetchRecapMaterials());
  }, [dispatch]);

  // useEffect(() => {
  //     if (cohortSlug && lastFetchedSlug.current !== cohortSlug) {
  //         dispatch(fetchRecapMaterialByCohortSlug(cohortSlug));
  //         lastFetchedSlug.current = cohortSlug;
  //     }
  // }, [dispatch, cohortSlug]);

  // Helper function to format date as d m y
  const formatDateDMY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Helper function to determine resource type from file_path
  const getResourceType = (filePath) => {
    if (!filePath) return { type: "unknown", label: "Unknown", icon: "📄" };
    
    const lowerPath = filePath.toLowerCase();
    if (lowerPath.includes("zoom.us") || lowerPath.includes("zoom")) {
      return { type: "zoom", label: "Zoom Recording", icon: "🎥" };
    }
    if (lowerPath.includes("drive.google.com") || lowerPath.includes("docs.google.com")) {
      return { type: "drive", label: "Google Drive", icon: "📁" };
    }
    if (lowerPath.includes("youtube.com") || lowerPath.includes("youtu.be")) {
      return { type: "youtube", label: "YouTube", icon: "▶️" };
    }
    return { type: "file", label: "File", icon: "📄" };
  };

  // Group materials by week_number
  const groupedMaterials = useMemo(() => {
    if (!recapMaterials || recapMaterials.length === 0) return {};
    
    const groups = {};
    recapMaterials.forEach((material) => {
      const weekNum = material.week_number || "general";
      if (!groups[weekNum]) {
        groups[weekNum] = [];
      }
      groups[weekNum].push(material);
    });
    
    // Sort weeks in descending order, but "general" goes last
    const sortedWeeks = Object.keys(groups).sort((a, b) => {
      if (a === "general") return 1;
      if (b === "general") return -1;
      return Number(b) - Number(a);
    });
    
    const sortedGroups = {};
    sortedWeeks.forEach(week => {
      sortedGroups[week] = groups[week];
    });
    
    return sortedGroups;
  }, [recapMaterials]);

  const weeks = Object.keys(groupedMaterials).sort((a, b) => {
    if (a === "general") return 1;
    if (b === "general") return -1;
    return Number(b) - Number(a);
  });

  // Get display label for week
  const getWeekLabel = (week) => {
    if (week === "general") return "General";
    return `Week ${week}`;
  };

  return (
    <>
      <div className="container p-4">
        <div className="recap-video-head-container mb-4">
          <h2 className="recap-video-heading">Class Recap Videos</h2>
          <p>
            Class Recap Videos provide concise video summaries of classroom
            lessons. They help you to review key concepts and reinforce learning
            outside class time.
          </p>
        </div>

        <div className="row">
          {recapMaterials.length === 0 && !loading ? (
            <p className="text-muted small mb-0">
              No recap materials available.
            </p>
          ) : (
            // Group by weeks
            weeks.map((week) => (
              <div key={week} className="col-12 week-group mb-4">
                <div className="week-header">
                  <h3 className="week-title">
                    {getWeekLabel(week)}
                  </h3>
                </div>
                {groupedMaterials[week].map((material) => {
                  const resourceType = getResourceType(material.file_path);
                  return (
                  <div key={material.id} className="col-lg-6">
                    <Link  className="recap-video-card" to={`/organization/recap-videos/${material.slug}`}  tabIndex={0}
                    >
                      <div className="recap-card-content">
                        <div className="recap-card-header">
                          <h4 className="recap-video-head mb-0">
                            {loading ? (
                              <Skeleton width={100} />
                            ) : (
                              (material.title ? material.title.slice(0, 60) : "Untitled Recap")
                            )}
                          </h4>
                          <span className={`resource-badge resource-${resourceType.type}`}>
                            {resourceType.icon} {resourceType.label}
                          </span>
                          {material.cohort_id === null && (
                            <span className="badge bg-primary-subtle text-primary ms-2 border-0" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                              Private Class
                            </span>
                          )}
                        </div>

                        <span className="recap-video-span mb-0">
                          {loading ? (
                            <Skeleton width={80} />
                          ) : (
                            formatDateDMY(material.created_at)
                          )}
                        </span>

                        <p className="recap-video-description">
                          {loading ? (
                            <Skeleton count={2} />
                          ) : (
                            (material.description
                              ? material.description.slice(0, 100)
                              : "")
                          )}
                        </p>
                      </div>
                    </Link>
                  </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default OrgList;
